import { isArray, isBoolean, isDefined, isNumber, isObject, isString } from 'ionic-angular/util/util';
import * as Q from 'q';
import { BaseObject } from '../business-objects/base-object/base-object.model';
import { isInArray } from './is-in-array';
import { Model } from './model';

export const oneAtATime = (fns: Array<() => Promise<any>>): Promise<any> => {
    return fns.reduce<any>(Q.when, Q());
};

export const mergeDedupe = (...a0) => {
    const set = new Set([].concat(...a0));
    const result = [];
    set.forEach(item => result.push(item));
    return result;
};

export const omit = (obj: any, omitKey: number | Array<number> | string | Array<string>): any => {
    return Object.keys(obj).reduce((result, key) => {
        if (isArray(omitKey)) {
            if ((omitKey as Array<number | string>).indexOf(key) === -1) {
                result[key] = obj[key];
            }
        } else {
            if (key !== omitKey.toString()) {
                result[key] = obj[key];
            }
        }
        return result;
    }, {});
};

export const intersect = (a, b): Array<any> => {
    return a.filter(x => b.indexOf(x) !== -1);
};

export const condtitionallyIntersect = (a, b) => {
    return a.filter(
        x => {
            return ((b.indexOf(x) !== -1)
                || (typeof x === 'function' ? b.some(x) : false));
        });
};

export const isSubset = (a, b) => {
    return intersect(a, b).length === a.length;
};

export const isConditionallySubset = (a, b) => {
    return condtitionallyIntersect(a, b).length === a.length;
};

export const getSortByFn = (sortBy: string, direction: number, mapToSortableValue: (sortBy: string, item: BaseObject) => string | number) => {
    return ((a, b) => {
        if (direction) {
            return compare(mapToSortableValue(sortBy, a), mapToSortableValue(sortBy, b));
        } else {
            return compare(mapToSortableValue(sortBy, b), mapToSortableValue(sortBy, a));
        }
    });
};

export const compareModel = (property, a: Model, b: Model) => {
    return compare(a.get(property), b.get(property));
};

export const compare = (a, b) => {
    if (!defined(a) && !defined(b)) {
        return 0;
    } else if (defined(a) && !defined(b)) {
        return 1;
    } else if (!defined(a) && defined(b)) {
        return -1;
    } else if (isString(a)) {
        return a.localeCompare(b.toString());
    } else if (isNumber(a)) {
        return a - b;
    }
    return 0;
};

export const defined = value => {
    return typeof value !== 'undefined';
};

export const deepCopy = obj => {
    let copy;

    // Handle the 3 simple types, and undefined or undefined
    if (obj === undefined || typeof obj !== 'object') return obj;

    // Handle Date
    if (obj instanceof Date) {
        copy = new Date();
        copy.setTime(obj.getTime());
        return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
        copy = [];
        for (let i = 0, len = obj.length; i < len; i++) {
            copy[i] = deepCopy(obj[i]);
        }
        return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
        copy = {};
        for (const attr in obj) {
            if (obj.hasOwnProperty(attr)) copy[attr] = deepCopy(obj[attr]);
        }
        return copy;
    }

    throw new Error("Unable to copy obj! Its type isn't supported.");
};

export const cleanObjectForDiff = (object: any): any => {

    const objectClean = {};

    if (!object) {
        return object;
    }

    const keysToIgnore = [
        'modified',
        'modifiedBy',
        'serverModified'
    ];

    Object.keys(object)
        .filter(key => isDefined(object[key]) && !isInArray(key, keysToIgnore))
        .forEach(key => objectClean[key] = object[key]);

    return objectClean;
};

export const isEntitySubResource = (entity: string): boolean => {
    return isInArray(entity, ['field', 'file', 'note', 'task']);
};

export const isEntityCategory = (entity: string): boolean => {
    return isInArray(entity, ['jobStatus', 'jobType', 'accessGroup', 'contactType', 'lookupType']);
};

export const isBaseObject = (x: any): x is BaseObject => {

    if (!isObject(x)) {
        return false;
    }

    return isDefined(x.id);
};

export const isVersionableEntity = (entity: string): boolean => {
    return isInArray(entity, ['job', 'field']);
};

export const isEmailAddress = (candidate: string): boolean => {

    if (!candidate || !isString(candidate)) return false;

    return !!candidate.match(/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/);
};

export const getAppMode = (): string => {
    switch (true) {
        case !document.URL.startsWith('http'):
        case document.URL.startsWith('http://localhost:8080'):
            // case document.URL.endsWith("8100/"):
            // case document.URL.endsWith("8101/"):
            return 'app';
        default:
            return 'desktop';
    }
};

export const stripHtml = (html: string, maxChars?: number): string => {
    const elem = document.createElement('div');
    elem.innerHTML = html;
    const value = elem.textContent || elem.innerText || '';
    const suffix = value.length > maxChars ? '...' : '';

    if (maxChars) {
        return value.substring(0, maxChars) + suffix;
    } else {
        return value;
    }
};

export const isBlob = (object): boolean => {
    return object instanceof Blob || object instanceof File;
};

export const parseBool = (value: string | number | boolean): boolean => {

    if (isBoolean(value)) {
        return value;
    }

    value = isNumber(value) ? value.toString() : value;

    switch (value) {
        case '1':
            return true;
        case '0':
            return false;
        default:
            return undefined;
    }
};



// WEBPACK FOOTER //
// ./src/core/util/utils.ts